import React from 'react';
import { motion } from 'framer-motion';
import { WhatsNewSlide } from '../../core/interface/whats-new.interface';
import FeatureCard from './FeatureCard';

interface FeatureSlideProps {
    slide: WhatsNewSlide;
}

const cardListVariants = {
    animate: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
};

const cardVariants = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
};

/** One slide's content — gradient icon badge, headline, description, and staggered feature cards. */
const FeatureSlide: React.FC<FeatureSlideProps> = ({ slide }) => {
    if (slide.image) {
        return (
            <div className='flex flex-col items-center'>
                <img src={slide.image.mobile} alt={slide.image.alt} className='block w-full sm:hidden' />
                <img src={slide.image.desktop} alt={slide.image.alt} className='hidden w-full sm:block' />
            </div>
        );
    }

    const Icon = slide.icon!;
    const accent = slide.accentColor ?? '#00aae5';

    return (
        <div className='flex flex-col items-center px-6 py-8 text-center sm:px-10'>
            <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className='mb-6 flex items-center justify-center rounded-full'
                style={{
                    width: 112,
                    height: 112,
                    background: `radial-gradient(circle at 35% 30%, ${accent}33, ${accent}0D 70%)`,
                }}
            >
                <Icon size={44} color={accent} strokeWidth={1.75} />
            </motion.div>

            <h2 className='mb-2 text-2xl font-bold leading-tight text-gray-900'>{slide.title}</h2>
            <p className='mx-auto mb-6 max-w-[440px] text-[15px] leading-6 text-gray-500'>{slide.description}</p>

            {!!slide.features?.length && (
                <motion.div
                    variants={cardListVariants}
                    initial='initial'
                    animate='animate'
                    className='flex w-full max-w-[440px] flex-col gap-2.5'
                >
                    {slide.features.map((feature, i) => (
                        <FeatureCard key={i} feature={feature} accentColor={accent} variants={cardVariants} />
                    ))}
                </motion.div>
            )}
        </div>
    );
};

export default FeatureSlide;
